/**
 * Auto-translate arbitrary REFERENCE/CATALOG text (drug names, lab/radiology test names,
 * master-data labels, generic CPOE order item names, etc.) via the LibreTranslate-backed,
 * DB-cached endpoint (backend POST /translate/auto — see translate.module.ts).
 *
 * This is deliberately NOT for clinician-typed free text (consultation notes, instructions,
 * discharge advice). That content stays exactly as the clinician typed it, in whatever
 * language — the earlier AI note-translation feature was removed at the user's explicit
 * request ("no need any Ai" / "no automatic translation"). This hook is a separate, later
 * addition scoped specifically to fixed catalog/reference values coming back from the API,
 * where a name always means the same thing regardless of who's viewing it.
 *
 * Caching has two layers:
 *   1. Server-side: entity_translations, keyed by a hash of the source text — so the very
 *      first time any user sees a given lab test / drug / label in a given language,
 *      LibreTranslate is called once and the result is saved; every later view (by anyone,
 *      any session) is a plain DB read.
 *   2. Client-side (this module): an in-memory Map for this browser tab/session, so a list of
 *      30 rows re-rendering doesn't re-issue 30 duplicate network calls.
 *
 * If LibreTranslate isn't configured on this server, POST /translate/auto still returns
 * normally when target==='en' (as a no-op) or fails quietly for other languages — this hook
 * always falls back to the original text rather than showing an error or blank space.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const memCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

function cacheKey(text: string, lang: string) {
  return `${lang}::${text}`;
}

async function fetchAutoTranslation(text: string, lang: string): Promise<string> {
  const key = cacheKey(text, lang);
  if (memCache.has(key)) return memCache.get(key)!;
  const existing = inFlight.get(key);
  if (existing) return existing;
  const p = api
    .translateAuto(text, lang)
    .then((r: any) => {
      const translated = r?.data?.translatedText || text;
      memCache.set(key, translated);
      inFlight.delete(key);
      return translated;
    })
    .catch(() => {
      inFlight.delete(key);
      // Don't keep retrying a failing/unconfigured translation server on every render —
      // remember "no translation available" for this text+language for the rest of the
      // session and just show the original text.
      memCache.set(key, text);
      return text;
    });
  inFlight.set(key, p);
  return p;
}

/**
 * Returns `text` translated into the current UI language (falls back to the original text
 * while the translation is loading, on failure, or when the UI language is English).
 */
export function useAutoTranslate(text: string | undefined | null): string {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const original = text || '';
  const [display, setDisplay] = useState(original);

  useEffect(() => {
    if (!original || lang === 'en') {
      setDisplay(original);
      return;
    }
    const key = cacheKey(original, lang);
    if (memCache.has(key)) {
      setDisplay(memCache.get(key)!);
      return;
    }
    let alive = true;
    setDisplay(original); // show original immediately while translation loads
    fetchAutoTranslation(original, lang).then((translated) => {
      if (alive) setDisplay(translated);
    });
    return () => {
      alive = false;
    };
  }, [original, lang]);

  return display;
}

/**
 * Non-hook helper for translating a batch of strings (e.g. every row in a freshly-fetched
 * list) up front, returning a lookup map original -> translated. Prefer this over calling
 * useAutoTranslate in a loop when rendering a list, so all the network calls fire together
 * instead of being staggered one-hook-at-a-time.
 */
export async function autoTranslateBatch(texts: string[], lang: string): Promise<Record<string, string>> {
  const langCode = (lang || 'en').split('-')[0];
  const unique = Array.from(new Set(texts.filter(Boolean)));
  const result: Record<string, string> = {};
  if (langCode === 'en') {
    unique.forEach((t) => (result[t] = t));
    return result;
  }
  await Promise.all(
    unique.map(async (t) => {
      result[t] = await fetchAutoTranslation(t, langCode);
    }),
  );
  return result;
}
